import { Injectable, signal } from '@angular/core';

export interface Toast {
  id: number;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  duration: number;
  isSyncing?: boolean; // Flag for syncing toast with animated spinner
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private readonly toasts = signal<Toast[]>([]);
  private nextId = 1;

  getToasts() {
    return this.toasts.asReadonly();
  }

  show(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info', duration = 5000): void {
    const id = this.nextId++;
    const toast: Toast = { id, message, type, duration };
    
    this.toasts.update(toasts => [...toasts, toast]);

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        this.dismiss(id);
      }, duration);
    }
  }

  success(message: string, duration = 5000): void {
    this.show(message, 'success', duration);
  }

  error(message: string, duration = 5000): void {
    this.show(message, 'error', duration);
  }

  warning(message: string, duration = 5000): void {
    this.show(message, 'warning', duration);
  }

  info(message: string, duration = 5000): void {
    this.show(message, 'info', duration);
  }

  /**
   * Show a persistent syncing toast with animated spinner
   * Returns the toast ID so it can be dismissed later
   */
  showSyncing(message: string): number {
    const id = this.nextId++;
    const toast: Toast = { 
      id, 
      message, 
      type: 'info', 
      duration: 0, // 0 = persistent, must be manually dismissed
      isSyncing: true 
    };
    
    this.toasts.update(toasts => [...toasts, toast]);
    return id;
  }

  dismiss(id: number): void {
    this.toasts.update(toasts => toasts.filter(t => t.id !== id));
  }

  clear(): void {
    this.toasts.set([]);
  }
}

